import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { supplyAgentService } from '@/services/supplyAgentService';
import {
  completionStrategyKey,
  completionStrategyOverlay,
  isCompletionRule,
  isReductionRule,
  normalizeSupplyAgentRules,
  reductionStrategyKey,
  reductionStrategyOverlay,
  withCompletionStrategy,
  withReductionStrategy,
  type SupplyAgentCompletionStrategy,
  type SupplyAgentReductionStrategy,
  type SupplyAgentRuleRecord,
} from '@/modules/supply/components/supplyAgentRuleUtils';
import {
  activeSessionEphemeralRules,
  isSessionEphemeralRuleEnabled,
  withSessionEphemeralEnabled,
} from '@/modules/supply/components/supplyAgentSessionLabels';

export type { SupplyAgentCompletionStrategy, SupplyAgentReductionStrategy };

interface UseSupplyAgentPersistedRulesOptions {
  supplierId: number | null;
  warehouseId: number;
  refreshToken?: number;
  autoSimulate?: boolean;
  /** Si True (toggle MP/MOQ tabla): al ajustar, respetar MP de línea. No es redondeo masivo del pedido. */
  applyLineBoxMultiples?: boolean;
}

function activeRuleIds(rules: SupplyAgentRuleRecord[]): number[] {
  return rules
    .filter((r) => r.id != null && r.is_active !== false)
    .map((r) => r.id as number);
}

export function useSupplyAgentPersistedRules({
  supplierId,
  warehouseId,
  refreshToken = 0,
  autoSimulate = true,
  applyLineBoxMultiples = false,
}: UseSupplyAgentPersistedRulesOptions) {
  const { i18n } = useTranslation();
  const queryClient = useQueryClient();
  const warehouseMode = supplierId == null;
  const bumpRulesVisibility = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['supply-agent-supplier-rules'] });
    void queryClient.invalidateQueries({ queryKey: ['supply-agent-rules-catalog'] });
  }, [queryClient]);
  const [rules, setRules] = useState<SupplyAgentRuleRecord[]>([]);
  const [sessionEnabledIds, setSessionEnabledIds] = useState<Set<number>>(new Set());
  const [loadingRules, setLoadingRules] = useState(false);
  const [simulating, setSimulating] = useState(false);
  const [deletingRuleId, setDeletingRuleId] = useState<number | null>(null);
  const [togglingRuleId, setTogglingRuleId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [simulation, setSimulation] = useState<Awaited<
    ReturnType<typeof supplyAgentService.simulate>
  > | null>(null);
  const [warehouseSimulation, setWarehouseSimulation] = useState<Awaited<
    ReturnType<typeof supplyAgentService.warehouseSimulate>
  > | null>(null);

  const sessionRef = useRef(sessionEnabledIds);
  sessionRef.current = sessionEnabledIds;
  const rulesRef = useRef(rules);
  rulesRef.current = rules;

  const [sessionEphemeralRules, setSessionEphemeralRules] = useState<Record<string, unknown>[]>([]);
  const sessionEphemeralRef = useRef(sessionEphemeralRules);
  sessionEphemeralRef.current = sessionEphemeralRules;
  const [completionStrategy, setCompletionStrategyState] = useState<SupplyAgentCompletionStrategy | null>(
    null,
  );
  const completionStrategyRef = useRef(completionStrategy);
  completionStrategyRef.current = completionStrategy;
  const [ruleCompletionStrategies, setRuleCompletionStrategies] = useState<
    Record<string, SupplyAgentCompletionStrategy>
  >({});
  const ruleCompletionStrategiesRef = useRef(ruleCompletionStrategies);
  ruleCompletionStrategiesRef.current = ruleCompletionStrategies;

  const [reductionStrategy, setReductionStrategyState] = useState<SupplyAgentReductionStrategy | null>(
    null,
  );
  const reductionStrategyRef = useRef(reductionStrategy);
  reductionStrategyRef.current = reductionStrategy;
  const [ruleReductionStrategies, setRuleReductionStrategies] = useState<
    Record<string, SupplyAgentReductionStrategy>
  >({});
  const ruleReductionStrategiesRef = useRef(ruleReductionStrategies);
  ruleReductionStrategiesRef.current = ruleReductionStrategies;

  const stampEphemeralCompletionStrategies = useCallback(
    (
      ephemeralRules: Record<string, unknown>[],
      availableRules: SupplyAgentRuleRecord[],
      enabledIds: Set<number>,
    ): Record<string, unknown>[] => {
      const pedido = completionStrategyRef.current;
      const perRule = ruleCompletionStrategiesRef.current;
      const pedidoRed = reductionStrategyRef.current;
      const perRuleRed = ruleReductionStrategiesRef.current;
      const next = ephemeralRules.map((rule) => {
        if (isCompletionRule(rule)) {
          const key = completionStrategyKey(rule);
          const strategy = (key ? perRule[key] : undefined) ?? pedido;
          return strategy ? withCompletionStrategy(rule, strategy) : rule;
        }
        if (isReductionRule(rule)) {
          const key = reductionStrategyKey(rule);
          const strategy = (key ? perRuleRed[key] : undefined) ?? pedidoRed;
          return strategy ? withReductionStrategy(rule, strategy) : rule;
        }
        return rule;
      });
      for (const rule of availableRules) {
        if (rule.id == null || rule.is_active === false || !enabledIds.has(rule.id)) continue;
        if (isCompletionRule(rule)) {
          const key = completionStrategyKey(rule);
          const strategy = (key ? perRule[key] : undefined) ?? pedido;
          if (strategy) {
            next.push(completionStrategyOverlay(String(rule.rule_type), strategy));
          }
        } else if (isReductionRule(rule)) {
          const key = reductionStrategyKey(rule);
          const strategy = (key ? perRuleRed[key] : undefined) ?? pedidoRed;
          if (strategy) {
            next.push(reductionStrategyOverlay(String(rule.rule_type), strategy));
          }
        }
      }
      return next;
    },
    [],
  );

  const runWarehouseSimulate = useCallback(
    async (
      enabledIds: Set<number>,
      availableRules: SupplyAgentRuleRecord[],
      ephemeralRules: Record<string, unknown>[] = sessionEphemeralRef.current,
    ) => {
      const ids = availableRules
        .filter((r) => r.id != null && r.is_active !== false && enabledIds.has(r.id))
        .map((r) => r.id as number);
      const ephemeralForApi = activeSessionEphemeralRules(ephemeralRules);

      if (!autoSimulate || (ids.length === 0 && ephemeralForApi.length === 0)) {
        setWarehouseSimulation(null);
        return null;
      }

      setSimulating(true);
      try {
        const data = await supplyAgentService.warehouseSimulate({
          warehouse_id: warehouseId,
          active_rule_ids: ids,
          ephemeral_rules: ephemeralForApi.length > 0 ? ephemeralForApi : undefined,
        });
        setWarehouseSimulation(data);
        return data;
      } finally {
        setSimulating(false);
      }
    },
    [autoSimulate, warehouseId],
  );

  const runSimulate = useCallback(
    async (
      enabledIds: Set<number>,
      availableRules: SupplyAgentRuleRecord[],
      ephemeralRules: Record<string, unknown>[] = sessionEphemeralRef.current,
    ) => {
      if (warehouseMode || supplierId == null) {
        setSimulation(null);
        return runWarehouseSimulate(enabledIds, availableRules, ephemeralRules);
      }

      const ids = availableRules
        .filter((r) => r.id != null && r.is_active !== false && enabledIds.has(r.id))
        .map((r) => r.id as number);
      const ephemeralForApi = stampEphemeralCompletionStrategies(
        activeSessionEphemeralRules(ephemeralRules),
        availableRules,
        enabledIds,
      );

      if (!autoSimulate || (ids.length === 0 && ephemeralForApi.length === 0)) {
        setSimulation(null);
        setWarehouseSimulation(null);
        return null;
      }

      setSimulating(true);
      try {
        const data = await supplyAgentService.simulate({
          supplier_id: supplierId,
          warehouse_id: warehouseId,
          use_persisted_rules: true,
          apply_line_box_multiples: applyLineBoxMultiples,
          active_rule_ids: ids,
          ephemeral_rules: ephemeralForApi.length > 0 ? ephemeralForApi : undefined,
          locale: i18n.language,
          completion_strategy: completionStrategyRef.current ?? undefined,
          reduction_strategy: reductionStrategyRef.current ?? undefined,
        });
        setSimulation(data);
        setWarehouseSimulation(null);
        return data;
      } finally {
        setSimulating(false);
      }
    },
    [
      autoSimulate,
      supplierId,
      warehouseId,
      warehouseMode,
      runWarehouseSimulate,
      i18n.language,
      applyLineBoxMultiples,
      stampEphemeralCompletionStrategies,
    ],
  );

  const reload = useCallback(async () => {
    setLoadingRules(true);
    setError(null);
    try {
      const response = await supplyAgentService.listRules(supplierId, warehouseId, {
        includeInactive: true,
      });
      const normalized = normalizeSupplyAgentRules(response.rules);
      const prevEnabled = sessionRef.current;
      const prevIds = new Set(
        rulesRef.current.map((r) => r.id).filter((id): id is number => id != null),
      );
      const nextEnabled = new Set<number>();
      for (const rule of normalized) {
        if (rule.id == null || rule.is_active === false) continue;
        const isNew = !prevIds.has(rule.id);
        if (prevEnabled.size === 0 || isNew || prevEnabled.has(rule.id)) {
          nextEnabled.add(rule.id);
        }
      }

      setRules(normalized);
      setSessionEnabledIds(nextEnabled);
      try {
        await runSimulate(nextEnabled, normalized);
      } catch (simErr) {
        const simMessage =
          simErr && typeof simErr === 'object' && 'message' in simErr
            ? String((simErr as { message?: string }).message || '')
            : simErr instanceof Error
              ? simErr.message
              : '';
        setError(simMessage || 'Error al simular el ajuste');
        setSimulation(null);
      }
      return normalized;
    } catch (e) {
      const message =
        e && typeof e === 'object' && 'message' in e
          ? String((e as { message?: string }).message || '')
          : e instanceof Error
            ? e.message
            : '';
      setError(message || 'Error al cargar reglas');
      return [];
    } finally {
      setLoadingRules(false);
    }
  }, [supplierId, warehouseId, runSimulate]);

  const deleteRule = useCallback(
    async (ruleId: number) => {
      setDeletingRuleId(ruleId);
      setError(null);
      try {
        await supplyAgentService.deleteRule(ruleId, supplierId, warehouseId);
        await reload();
      } catch (e) {
        const message = e instanceof Error ? e.message : 'Error al eliminar regla';
        setError(message);
        throw e;
      } finally {
        setDeletingRuleId(null);
      }
    },
    [supplierId, warehouseId, reload],
  );

  const purgeRule = useCallback(
    async (ruleId: number) => {
      setDeletingRuleId(ruleId);
      setError(null);
      try {
        await supplyAgentService.deleteRule(ruleId, supplierId, warehouseId, { hard: true });
        await reload();
      } catch (e) {
        const message = e instanceof Error ? e.message : 'Error al borrar regla';
        setError(message);
        throw e;
      } finally {
        setDeletingRuleId(null);
      }
    },
    [supplierId, warehouseId, reload],
  );

  const purgeInactiveRules = useCallback(async () => {
    if (supplierId == null) {
      setError('Filtrá un proveedor para limpiar reglas inactivas de alcance proveedor.');
      return;
    }
    setError(null);
    try {
      await supplyAgentService.purgeInactiveRules(supplierId, warehouseId);
      await reload();
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Error al limpiar inactivas';
      setError(message);
      throw e;
    }
  }, [supplierId, warehouseId, reload]);

  const setSessionRuleEnabled = useCallback(
    (ruleId: number, enabled: boolean) => {
      setSessionEnabledIds((prev) => {
        const next = new Set(prev);
        if (enabled) next.add(ruleId);
        else next.delete(ruleId);
        void runSimulate(next, rulesRef.current).catch((e) => {
          const message = e instanceof Error ? e.message : 'Error al simular';
          setError(message);
        });
        return next;
      });
    },
    [runSimulate],
  );

  const setPersistedRuleActive = useCallback(
    async (ruleId: number, isActive: boolean) => {
      setTogglingRuleId(ruleId);
      setError(null);
      try {
        await supplyAgentService.patchRule(ruleId, supplierId, warehouseId, {
          is_active: isActive,
        });
        // Actualización optimista: no bloquear el toggle hasta list+simulación.
        setRules((prev) =>
          prev.map((rule) => (rule.id === ruleId ? { ...rule, is_active: isActive } : rule)),
        );
        bumpRulesVisibility();
        setSessionEnabledIds((prev) => {
          const next = new Set(prev);
          if (isActive) next.add(ruleId);
          else next.delete(ruleId);
          return next;
        });
        const nextEnabled = new Set(sessionRef.current);
        if (isActive) nextEnabled.add(ruleId);
        else nextEnabled.delete(ruleId);
        const nextRules = rulesRef.current.map((rule) =>
          rule.id === ruleId ? { ...rule, is_active: isActive } : rule,
        );
        void runSimulate(nextEnabled, nextRules).catch((e) => {
          const message = e instanceof Error ? e.message : 'Error al simular';
          setError(message);
        });
      } catch (e) {
        const message = e instanceof Error ? e.message : 'Error al actualizar regla';
        setError(message);
        throw e;
      } finally {
        setTogglingRuleId(null);
      }
    },
    [supplierId, warehouseId, runSimulate, bumpRulesVisibility],
  );

  const applySessionEphemeralRules = useCallback(
    (incoming: Record<string, unknown>[]) => {
      const next = [...sessionEphemeralRef.current];
      for (const rule of incoming) {
        const rtype = String(rule.rule_type || '');
        const idx = next.findIndex((r) => String(r.rule_type || '') === rtype);
        // Reaplicar desde el chat deja la regla activa para este pedido.
        const normalized = withSessionEphemeralEnabled(rule, true);
        if (idx >= 0) next[idx] = normalized;
        else next.push(normalized);
      }
      setSessionEphemeralRules(next);
      void runSimulate(sessionRef.current, rulesRef.current, next).catch((e) => {
        const message = e instanceof Error ? e.message : 'Error al simular';
        setError(message);
      });
    },
    [runSimulate],
  );

  const clearSessionEphemeralRules = useCallback(() => {
    setSessionEphemeralRules([]);
    void runSimulate(sessionRef.current, rulesRef.current, []).catch((e) => {
      const message = e instanceof Error ? e.message : 'Error al simular';
      setError(message);
    });
  }, [runSimulate]);

  const removeSessionEphemeralRule = useCallback(
    (index: number) => {
      const next = sessionEphemeralRef.current.filter((_, i) => i !== index);
      setSessionEphemeralRules(next);
      void runSimulate(sessionRef.current, rulesRef.current, next).catch((e) => {
        const message = e instanceof Error ? e.message : 'Error al simular';
        setError(message);
      });
    },
    [runSimulate],
  );

  const setSessionEphemeralRuleEnabled = useCallback(
    (index: number, enabled: boolean) => {
      const next = sessionEphemeralRef.current.map((rule, i) =>
        i === index ? withSessionEphemeralEnabled(rule, enabled) : rule,
      );
      setSessionEphemeralRules(next);
      void runSimulate(sessionRef.current, rulesRef.current, next).catch((e) => {
        const message = e instanceof Error ? e.message : 'Error al simular';
        setError(message);
      });
    },
    [runSimulate],
  );

  const setCompletionStrategy = useCallback(
    (strategy: SupplyAgentCompletionStrategy) => {
      const hadPerRule = Object.keys(ruleCompletionStrategiesRef.current).length > 0;
      setRuleCompletionStrategies({});
      ruleCompletionStrategiesRef.current = {};
      if (completionStrategyRef.current === strategy && !hadPerRule) return;
      setCompletionStrategyState(strategy);
      completionStrategyRef.current = strategy;
      void runSimulate(sessionRef.current, rulesRef.current).catch((e) => {
        const message = e instanceof Error ? e.message : 'Error al simular';
        setError(message);
      });
    },
    [runSimulate],
  );

  const setRuleCompletionStrategy = useCallback(
    (rule: SupplyAgentRuleRecord, strategy: SupplyAgentCompletionStrategy) => {
      const key = completionStrategyKey(rule);
      if (!key) return;
      const next = { ...ruleCompletionStrategiesRef.current, [key]: strategy };
      setRuleCompletionStrategies(next);
      ruleCompletionStrategiesRef.current = next;
      void runSimulate(sessionRef.current, rulesRef.current).catch((e) => {
        const message = e instanceof Error ? e.message : 'Error al simular';
        setError(message);
      });
    },
    [runSimulate],
  );

  const setReductionStrategy = useCallback(
    (strategy: SupplyAgentReductionStrategy) => {
      const hadPerRule = Object.keys(ruleReductionStrategiesRef.current).length > 0;
      setRuleReductionStrategies({});
      ruleReductionStrategiesRef.current = {};
      if (reductionStrategyRef.current === strategy && !hadPerRule) return;
      setReductionStrategyState(strategy);
      reductionStrategyRef.current = strategy;
      void runSimulate(sessionRef.current, rulesRef.current).catch((e) => {
        const message = e instanceof Error ? e.message : 'Error al simular';
        setError(message);
      });
    },
    [runSimulate],
  );

  const setRuleReductionStrategy = useCallback(
    (rule: SupplyAgentRuleRecord, strategy: SupplyAgentReductionStrategy) => {
      const key = reductionStrategyKey(rule);
      if (!key) return;
      if (ruleReductionStrategiesRef.current[key] === strategy) return;
      const next = { ...ruleReductionStrategiesRef.current, [key]: strategy };
      setRuleReductionStrategies(next);
      ruleReductionStrategiesRef.current = next;
      void runSimulate(sessionRef.current, rulesRef.current).catch((e) => {
        const message = e instanceof Error ? e.message : 'Error al simular';
        setError(message);
      });
    },
    [runSimulate],
  );

  useEffect(() => {
    setSessionEphemeralRules([]);
    setCompletionStrategyState(null);
    setRuleCompletionStrategies({});
    setReductionStrategyState(null);
    setRuleReductionStrategies({});
  }, [warehouseId]);

  useEffect(() => {
    setCompletionStrategyState(null);
    setRuleCompletionStrategies({});
    setReductionStrategyState(null);
    setRuleReductionStrategies({});
  }, [supplierId]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      await reload();
      if (cancelled) return;
    })();
    return () => {
      cancelled = true;
    };
  }, [reload, refreshToken]);

  useEffect(() => {
    bumpRulesVisibility();
  }, [rules, bumpRulesVisibility]);

  return {
    rules,
    sessionEnabledIds,
    sessionEphemeralRules,
    loadingRules,
    simulating,
    deletingRuleId,
    togglingRuleId,
    error,
    simulation,
    warehouseSimulation,
    warehouseMode,
    reload,
    deleteRule,
    purgeRule,
    purgeInactiveRules,
    setSessionRuleEnabled,
    setPersistedRuleActive,
    applySessionEphemeralRules,
    clearSessionEphemeralRules,
    removeSessionEphemeralRule,
    setSessionEphemeralRuleEnabled,
    isSessionEphemeralRuleEnabled,
    completionStrategy,
    setCompletionStrategy,
    ruleCompletionStrategies,
    setRuleCompletionStrategy,
    hasCompletionRule: (() => {
      const fromSaved = rules.some((rule) => {
        if (!isCompletionRule(rule) || rule.is_active === false) return false;
        return rule.id == null || sessionEnabledIds.has(rule.id);
      });
      if (fromSaved) return true;
      return sessionEphemeralRules.some((rule) => {
        if (!isSessionEphemeralRuleEnabled(rule)) return false;
        return isCompletionRule(rule);
      });
    })(),
    reductionStrategy,
    setReductionStrategy,
    ruleReductionStrategies,
    setRuleReductionStrategy,
    hasReductionRule: (() => {
      const fromSaved = rules.some((rule) => {
        if (!isReductionRule(rule) || rule.is_active === false) return false;
        return rule.id == null || sessionEnabledIds.has(rule.id);
      });
      if (fromSaved) return true;
      return sessionEphemeralRules.some((rule) => {
        if (!isSessionEphemeralRuleEnabled(rule)) return false;
        return isReductionRule(rule);
      });
    })(),
    setSimulation,
    setError,
    activeRuleIds: activeRuleIds(rules),
  };
}
